import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import RachelWidget from "./RachelWidget";

const NAV = [
  ["Home", "/"],
  ["Services", "/services"],
  ["Specialist", "/specialist"],
  ["Pricing", "/pricing"],
  ["Get a Quote", "/quote"],
  ["Book", "/book"],
  ["Commercial", "/commercial"],
  ["About", "/about"],
  ["Careers", "/careers"],
  ["Contact", "/contact"],
];

export function Emblem({ variant = "mark", className = "h-11" }) {
  if (variant === "full") {
    return (
      <Link to="/" data-testid="brand-logo" className="inline-flex group">
        <img src="/logo.png" alt="Black Diamond Elite Cleaning"
          className={`${className} w-auto object-contain group-hover:scale-105 transition-transform`} />
      </Link>
    );
  }
  return (
    <Link to="/" data-testid="brand-logo" className="flex items-center gap-2.5 group">
      <span className="block h-10 w-11 overflow-hidden shrink-0">
        <img src="/logo.png" alt="Black Diamond Elite Cleaning"
          className="w-16 max-w-none object-top object-cover group-hover:scale-105 transition-transform" style={{ height: "4rem" }} />
      </span>
      <span className="leading-none">
        <span className="block font-display text-lg tracking-wide text-[#fdfbf7]">BLACK DIAMOND</span>
        <span className="block text-[10px] tracking-luxe text-[#d4af37] mt-0.5">ELITE CLEANING</span>
      </span>
    </Link>
  );
}

export default function PublicLayout({ children }) {
  const [open, setOpen] = useState(false);
  const { user } = useAuth();
  const nav = useNavigate();

  const portalPath = () => {
    if (!user) return "/login";
    if (user.role === "customer") return "/portal";
    if (user.role === "cleaner") return "/cleaner";
    return "/admin";
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#fdfbf7]">
      <header className="sticky top-0 z-40 bg-[#0a0a0a] border-b border-[#333]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-[72px] flex items-center justify-between">
          <Emblem />
          <nav className="hidden lg:flex items-center gap-7">
            {NAV.map(([label, path]) => (
              <NavLink key={path} to={path} data-testid={`nav-${label.toLowerCase().replace(/ /g, "-")}`}
                className={({ isActive }) => `text-sm link-underline transition-colors ${isActive ? "text-[#d4af37]" : "text-[#e8e5de] hover:text-[#d4af37]"}`}>
                {label}
              </NavLink>
            ))}
          </nav>
          <div className="hidden lg:flex items-center gap-3">
            <button data-testid="header-login-btn" onClick={() => nav(portalPath())}
              className="btn-outline-gold text-sm px-4 py-2 rounded-sm">
              {user ? "My Account" : "Customer Login"}
            </button>
            <button data-testid="header-quote-btn" onClick={() => nav("/quote")} className="btn-gold text-sm px-4 py-2 rounded-sm">
              Get My Quote
            </button>
          </div>
          <button data-testid="mobile-menu-btn" className="lg:hidden text-[#fdfbf7]" onClick={() => setOpen(true)}>
            <Menu size={26} />
          </button>
        </div>
      </header>

      {/* Mobile slide-out */}
      <div className={`fixed inset-0 z-50 lg:hidden transition-opacity ${open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"}`}>
        <div className="absolute inset-0 bg-black/60" onClick={() => setOpen(false)} />
        <div className={`absolute right-0 top-0 h-full w-[82%] max-w-sm bg-[#0a0a0a] border-l border-[#333] p-6 transition-transform duration-300 ${open ? "translate-x-0" : "translate-x-full"}`}>
          <div className="flex items-center justify-between mb-8">
            <Emblem />
            <button data-testid="mobile-menu-close" onClick={() => setOpen(false)} className="text-[#fdfbf7]"><X size={24} /></button>
          </div>
          <nav className="flex flex-col gap-1">
            {NAV.map(([label, path]) => (
              <NavLink key={path} to={path} onClick={() => setOpen(false)} data-testid={`mnav-${label.toLowerCase().replace(/ /g, "-")}`}
                className={({ isActive }) => `py-3 border-b border-[#222] text-base ${isActive ? "text-[#d4af37]" : "text-[#e8e5de]"}`}>
                {label}
              </NavLink>
            ))}
          </nav>
          <div className="mt-8 flex flex-col gap-3">
            <button onClick={() => { setOpen(false); nav(portalPath()); }} className="btn-outline-gold py-3 rounded-sm">
              {user ? "My Account" : "Customer Login"}
            </button>
            <button onClick={() => { setOpen(false); nav("/quote"); }} className="btn-gold py-3 rounded-sm">Get My Quote</button>
          </div>
        </div>
      </div>

      <main className="flex-1">{children}</main>

      <footer className="bg-[#0a0a0a] text-[#a3a3a3] mt-20">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-14 grid grid-cols-1 md:grid-cols-4 gap-10">
          <div className="md:col-span-2">
            <Emblem variant="full" className="h-20" />
            <p className="mt-5 max-w-sm text-sm leading-relaxed">
              Professional residential and commercial cleaning across North London, NW London & Watford — with premium standards and carefully managed care.
            </p>
            <p className="mt-4 text-[#d4af37] font-display text-xl">Affordable Cleaning. Diamond Standards.</p>
          </div>
          <div>
            <p className="text-[#fdfbf7] text-sm font-semibold mb-4">Explore</p>
            <ul className="space-y-2 text-sm">
              {NAV.slice(1, 7).map(([l, p]) => <li key={p}><Link className="hover:text-[#d4af37]" to={p}>{l}</Link></li>)}
            </ul>
          </div>
          <div>
            <p className="text-[#fdfbf7] text-sm font-semibold mb-4">Legal</p>
            <ul className="space-y-2 text-sm">
              <li><Link className="hover:text-[#d4af37]" to="/legal/privacy">Privacy Policy</Link></li>
              <li><Link className="hover:text-[#d4af37]" to="/legal/cookie">Cookie Policy</Link></li>
              <li><Link className="hover:text-[#d4af37]" to="/legal/terms">Terms of Service</Link></li>
              <li><Link className="hover:text-[#d4af37]" to="/legal/cancellation">Cancellation Policy</Link></li>
              <li><Link className="hover:text-[#d4af37]" to="/legal/complaints">Complaints Policy</Link></li>
            </ul>
          </div>
        </div>
        <div className="border-t border-[#222] py-5 text-center text-xs text-[#666]">
          © {new Date().getFullYear()} Black Diamond Elite Cleaning · blackdiamondelitecleaning@gmail.com
          <span className="mx-2 text-[#333]">·</span>
          <Link to="/staff-login" data-testid="footer-staff-login" className="hover:text-[#d4af37]">Staff Login</Link>
          <span className="mx-2 text-[#333]">·</span>
          <Link to="/admin-login" data-testid="footer-owner-login" className="hover:text-[#d4af37]">Owner / Admin</Link>
        </div>
      </footer>
      <RachelWidget />
    </div>
  );
}
